import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { DATE_FORMAT_SHOW } from "src/utils/constants"
import { formatDecimalCaseReplaceComma } from "src/utils/format-decimal"

const ParameterMotorPrecosTPFCalculados = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        props.loading(true)

        try {
            const response = await dataLakeService.loadPaginationEndpoint(props.endpoint)
            const { results, count, next, previous } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            console.error("Erro ao carregar os dados:", error)
        } finally {
            props.loading(false)
        }
    }

    const createRow = async () => {
        const body = {
            CETIP: null,
            DataReferencia: null,
            Indexador: null,
            TIRAccrual: null,
            TaxaMTM: null,
            PUMTM: null,
            DurationAccrual: null,
            DurationMTM: null,
            DurationAno: null,
            PUPAR: null,
            PercentualPUPAR: null,
            LOGERRO: null
        }

        props.loading(true)

        try {
            await dataLakeService.createTableEndpoint(props.endpoint, body)
            await loadData()
        } catch (error) {
            console.error("Erro ao criar o registro:", error)
            throw error
        } finally {
            props.loading(false)
        }
    }

    const updateRow = async (newRow) => {
        const {
            ID, CETIP, DataReferencia, Indexador, TIRAccrual, TaxaMTM, PUMTM,
            DurationAccrual, DurationMTM, DurationAno, PUPAR, PercentualPUPAR, LOGERRO
        } = newRow

        const body = {
            CETIP,
            DataReferencia,
            Indexador,
            TIRAccrual,
            TaxaMTM,
            PUMTM,
            DurationAccrual,
            DurationMTM,
            DurationAno,
            PUPAR,
            PercentualPUPAR,
            LOGERRO
        }

        props.loading(true)

        try {
            await dataLakeService.updateTableEndpoint(props.endpoint, ID, body)
        } catch (error) {
            console.error("Erro ao atualizar o registro:", error)
            throw error
        } finally {
            props.loading(false)
        }
    }

    const deleteRow = async (row) => {
        const { ID } = row
        props.loading(true)

        try {
            await dataLakeService.deleteTableEndpoint(props.endpoint, ID)
            setData((currentData) => currentData.filter((item) => item.ID !== ID))
        } catch (error) {
            console.error("Erro ao excluir o registro:", error)
            throw error
        } finally {
            props.loading(false)
        }
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)

        try {
            const response = await dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset)
            const { results, count, next, previous } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            console.error("Erro ao filtrar os dados:", error)
        } finally {
            props.loading(false)
        }
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        try {
            const response = await dataLakeService.paginate(endpoint)
            const { results, count, previous, next } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            console.error("Erro ao alterar a página:", error)
        } finally {
            props.loading(false)
        }
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },

        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'DataReferencia',
            headerName: 'Data Referência',
            minWidth: 150,
            editable: true,
            typeComponent: 'date',
            disableColumnMenu: true,
            renderCell: (params) =>
                params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'Indexador',
            headerName: 'Indexador',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TIRAccrual',
            headerName: 'TIRAccrual',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'TaxaMTM',
            headerName: 'TaxaMTM',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'PUMTM',
            headerName: 'PUMTM',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'DurationAccrual',
            headerName: 'DurationAccrual',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'DurationMTM',
            headerName: 'DurationMTM',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'DurationAno',
            headerName: 'DurationAno',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'PUPAR',
            headerName: 'PUPAR',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'PercentualPUPAR',
            headerName: 'Percentual PUPAR',
            type: 'number',
            flex: true,
            minWidth: 130,
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            editable: true
        },
        {
            field: 'LOGERRO',
            headerName: 'LOGERRO',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true} />
        </div>
    )
}

ParameterMotorPrecosTPFCalculados.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
}

export default ParameterMotorPrecosTPFCalculados
